<template>
  <vs-popup id="popupRiskAnalysis" :title="$t('AI 위험도 분석 결과')" :active.sync="showPopup" :width="popupWidth">
    <div v-if="riskAnalysisData">
      <!-- PC 레이아웃 (768px 이상) -->
      <div class="desktop-layout" v-show="!isMobile">
        <div class="risk-analysis-container">
          <!-- 헤더 정보 -->
          <div class="header-info mb-6">
            <div class="risk-summary">
              <div class="summary-card">
                <div class="summary-header">
                  <feather-icon icon="BarChartIcon" size="20" class="mr-2" />
                  <span class="summary-title">{{$t('AI 분석 결과')}}</span>
                </div>
                <div class="risk-values">
                  <div class="risk-item">
                    <span class="risk-label">{{$t('가능성')}}</span>
                    <span class="risk-value possibility">{{ riskAnalysisData.가능성 }}</span>
                  </div>
                  <div class="risk-multiply">×</div>
                  <div class="risk-item">
                    <span class="risk-label">{{$t('중대성')}}</span>
                    <span class="risk-value severity">{{ riskAnalysisData.중대성 }}</span>
                  </div>
                  <div class="risk-equals">=</div>
                  <div class="risk-item">
                    <span class="risk-label">{{$t('위험성')}}</span>
                    <span class="risk-value total" :class="riskLevelClass">{{ riskAnalysisData.위험성 }}</span>
                  </div>
                </div>
                <div class="management-standard">
                  <div class="standard-badge" :class="riskLevelClass">
                    <feather-icon :icon="riskLevelIcon" size="16" class="mr-2" />
                    {{ riskAnalysisData.관리기준 }}{{ riskGroupSuffix }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 평가근거 상세 -->
          <div class="evaluation-details" v-if="riskAnalysisData.평가근거">
            <h4 class="section-title">
              <feather-icon icon="FileTextIcon" size="20" class="mr-2" />
              {{$t('평가근거')}}
            </h4>

            <div class="detail-grid">
              <div class="detail-card">
                <div class="detail-header">
                  <feather-icon icon="AlertTriangleIcon" size="16" class="mr-2 text-orange-500" />
                  <span class="detail-title">{{$t('가능성 평가근거')}}</span>
                </div>
                <div class="detail-content">
                  {{ riskAnalysisData.평가근거.가능성_근거 }}
                </div>
              </div>

              <div class="detail-card">
                <div class="detail-header">
                  <feather-icon icon="AlertOctagonIcon" size="16" class="mr-2 text-red-500" />
                  <span class="detail-title">{{$t('중대성 평가근거')}}</span>
                </div>
                <div class="detail-content">
                  {{ riskAnalysisData.평가근거.중대성_근거 }}
                </div>
              </div>
            </div>
          </div>

          <!-- AI 분석 안내 -->
          <div class="ai-info mt-6">
            <div class="ai-badge">
              <feather-icon icon="ZapIcon" size="16" class="mr-2" />
              {{$t('AI 분석 결과')}}
            </div>
            <p class="ai-description">{{$t('위 분석 결과는 AI가 제공된 정보를 바탕으로 위험성의 가능성과 중대성을 평가한 것입니다. 실제 적용 시에는 현장 상황을 고려하여 추가적인 검토가 필요할 수 있습니다.')}}</p>
          </div>
        </div>

        <!-- PC 하단 버튼 -->
        <div class="button-container">
          <vs-button color="primary" @click="handleClose"> {{$t('확인')}} </vs-button>
        </div>
      </div>

      <!-- 모바일 레이아웃 (768px 미만) -->
      <div class="mobile-layout" v-show="isMobile">
        <!-- 모바일 헤더 -->
        <div class="mobile-header mobile-sticky-header">
          <div class="header-title">
            <feather-icon icon="BarChartIcon" size="18" class="mr-2" />
            <span>{{$t('위험도 분석 결과')}}</span>
          </div>
        </div>

        <!-- 스크롤 가능한 컨텐츠 영역 -->
        <div class="mobile-content-wrapper">
          <!-- 모바일 위험도 요약 -->
          <div class="mobile-risk-summary">
            <div class="mobile-risk-calculation">
              <div class="mobile-calc-single-row">
                <div class="mobile-risk-factor">
                  <div class="factor-label">{{$t('가능성')}}</div>
                  <div class="factor-value possibility">{{ riskAnalysisData.가능성 }}</div>
                </div>
                <div class="mobile-operator">×</div>
                <div class="mobile-risk-factor">
                  <div class="factor-label">{{$t('중대성')}}</div>
                  <div class="factor-value severity">{{ riskAnalysisData.중대성 }}</div>
                </div>
                <div class="mobile-equals">=</div>
                <div class="mobile-total-risk">
                  <div class="total-label">{{$t('위험성')}}</div>
                  <div class="total-value" :class="riskLevelClass">{{ riskAnalysisData.위험성 }}</div>
                </div>
              </div>
            </div>
            <div class="mobile-management-standard">
              <div class="mobile-standard-badge" :class="riskLevelClass">
                <feather-icon :icon="riskLevelIcon" size="14" class="mr-2" />
                {{ riskAnalysisData.관리기준 }}{{ riskGroupSuffix }}
              </div>
            </div>
          </div>

          <!-- 모바일 평가근거 -->
          <div class="mobile-evaluation-section" v-if="riskAnalysisData.평가근거">
            <h4 class="mobile-section-title">
              <feather-icon icon="FileTextIcon" size="16" class="mr-2" />
              {{$t('평가근거')}}
            </h4>

            <div class="mobile-evaluation-cards">
              <div class="mobile-evaluation-card">
                <div class="mobile-evaluation-header">
                  <feather-icon icon="AlertTriangleIcon" size="14" class="mr-2 text-orange-500" />
                  <span class="mobile-evaluation-title">{{$t('가능성 평가근거')}}</span>
                </div>
                <div class="mobile-evaluation-content">
                  {{ riskAnalysisData.평가근거.가능성_근거 }}
                </div>
              </div>

              <div class="mobile-evaluation-card">
                <div class="mobile-evaluation-header">
                  <feather-icon icon="AlertOctagonIcon" size="14" class="mr-2 text-red-500" />
                  <span class="mobile-evaluation-title">{{$t('중대성 평가근거')}}</span>
                </div>
                <div class="mobile-evaluation-content">
                  {{ riskAnalysisData.평가근거.중대성_근거 }}
                </div>
              </div>
            </div>
          </div>

          <!-- 모바일 AI 안내 -->
          <div class="mobile-ai-info">
            <div class="mobile-ai-badge">
              <feather-icon icon="ZapIcon" size="14" class="mr-2" />
              {{$t('AI 분석 결과')}}
            </div>
            <p class="mobile-ai-description">{{$t('위 분석 결과는 AI가 제공된 정보를 바탕으로 위험성의 가능성과 중대성을 평가한 것입니다. 실제 적용 시에는 현장 상황을 고려하여 추가적인 검토가 필요할 수 있습니다.')}}</p>
          </div>

          <!-- 하단 여백 -->
          <div class="mobile-bottom-spacer"></div>
        </div>

        <!-- 모바일 하단 버튼 (sticky) -->
        <div class="mobile-button-container mobile-sticky-buttons">
          <vs-button color="primary" @click="handleClose" class="mobile-button mobile-button-primary"> {{$t('확인')}} </vs-button>
        </div>
      </div>
    </div>
  </vs-popup>
</template>

<script>
import { getRiskGroupByScore, getRiskGroupByLabel } from "@/util/RiskGroupUtil";

export default {
  name: "PopupRiskAnalysis",
  props: {
    value: Boolean,
    riskAnalysisData: {
      type: Object,
      default: null,
    },
    // 관리기준 표시 시 (상)/(중)/(하) 그룹 표기 여부
    showRiskGroup: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      windowWidth: window.innerWidth,
    };
  },
  computed: {
    showPopup: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value);
      },
    },
    isMobile() {
      return this.windowWidth < 768;
    },
    popupWidth() {
      return this.isMobile ? "95vw" : "800px";
    },
    riskGroupSuffix() {
      if (!this.showRiskGroup || !this.riskAnalysisData) return "";
      const group = getRiskGroupByScore(this.riskAnalysisData.위험성) || getRiskGroupByLabel(this.riskAnalysisData.관리기준);
      return group ? ` (${this.$t(group)})` : "";
    },
    riskLevelIcon() {
      const riskValue = (this.riskAnalysisData && this.riskAnalysisData.위험성) || 0;
      if (riskValue >= 16) return "FrownIcon"; // 매우높음
      if (riskValue >= 9) return "MehIcon"; // 높음/약간높음
      if (riskValue >= 4) return "MehIcon"; // 보통/낮음
      if (riskValue >= 1) return "SmileIcon"; // 매우낮음
      return "MehIcon";
    },
    riskLevelClass() {
      const riskValue = (this.riskAnalysisData && this.riskAnalysisData.위험성) || 0;
      if (riskValue >= 16) return "risk-very-high"; // 매우높음 - 빨간색
      if (riskValue >= 9) return "risk-high"; // 높음/약간높음 - 주황색
      if (riskValue >= 4) return "risk-medium"; // 보통/낮음 - 노란색
      if (riskValue >= 1) return "risk-low"; // 매우낮음 - 초록색
      return "risk-medium";
    },
  },
  mounted() {
    // 화면 크기 변경 감지
    this.handleResize = () => {
      this.windowWidth = window.innerWidth;
    };
    window.addEventListener("resize", this.handleResize);

    const backButtonRouteGuard = this.$router.beforeEach((to, from, next) => {
      if (this.showPopup) {
        this.showPopup = false;
        next(false);
        return;
      }
      next(true);
    });
    this.$once("hook:destroyed", () => {
      backButtonRouteGuard();
      window.removeEventListener("resize", this.handleResize);
    });
  },
  methods: {
    handleClose() {
      this.showPopup = false;
    },
  },
};
</script>

<style scoped lang="scss">
.risk-analysis-container {
  padding: 20px;
}

.header-info {
  .risk-summary {
    display: flex;
    justify-content: center;

    .summary-card {
      background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
      border-radius: 12px;
      border: 1px solid #e9ecef;
      padding: 24px;
      max-width: 600px;
      width: 100%;

      .summary-header {
        display: flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 20px;

        .summary-title {
          font-size: 18px;
          font-weight: 600;
          color: #333;
        }
      }

      .risk-values {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 16px;
        margin-bottom: 20px;

        .risk-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;

          .risk-label {
            font-size: 14px;
            color: #666;
            font-weight: 500;
          }

          .risk-value {
            font-size: 24px;
            font-weight: 700;
            padding: 8px 16px;
            border-radius: 8px;
            min-width: 50px;
            text-align: center;
            border: 2px solid;

            &.possibility {
              background: #e3f2fd;
              color: #1976d2;
              border-color: #42a5f5;
            }

            &.severity {
              background: #fff3e0;
              color: #f57c00;
              border-color: #ff9800;
            }

            &.total {
              &.risk-very-high {
                background: #ffebee;
                color: #c62828;
                border-color: #ef5350;
              }

              &.risk-high {
                background: #fff3e0;
                color: #ef6c00;
                border-color: #ff9800;
              }

              &.risk-medium {
                background: #fffde7;
                color: #f57f17;
                border-color: #ffeb3b;
              }

              &.risk-low {
                background: #e8f5e8;
                color: #2e7d32;
                border-color: #4caf50;
              }
            }
          }
        }

        .risk-multiply,
        .risk-equals {
          font-size: 20px;
          font-weight: 600;
          color: #666;
        }
      }

      .management-standard {
        display: flex;
        justify-content: center;

        .standard-badge {
          display: flex;
          align-items: center;
          padding: 12px 20px;
          border-radius: 20px;
          font-weight: 600;
          font-size: 16px;
          border: 1px solid;

          &.risk-very-high {
            background: #ffebee;
            color: #c62828;
            border-color: #ef5350;
          }

          &.risk-high {
            background: #fff3e0;
            color: #ef6c00;
            border-color: #ff9800;
          }

          &.risk-medium {
            background: #fffde7;
            color: #f57f17;
            border-color: #ffeb3b;
          }

          &.risk-low {
            background: #e8f5e8;
            color: #2e7d32;
            border-color: #4caf50;
          }
        }
      }
    }
  }
}

.evaluation-details {
  .section-title {
    display: flex;
    align-items: center;
    font-size: 18px;
    font-weight: 600;
    margin-bottom: 20px;
    color: #333;
  }

  .detail-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;

    .detail-card {
      background: #fff;
      border: 1px solid #e9ecef;
      border-radius: 8px;
      padding: 16px;
      transition: all 0.2s ease;

      &:hover {
        border-color: #007bff;
        box-shadow: 0 2px 8px rgba(0, 123, 255, 0.1);
      }

      .detail-header {
        display: flex;
        align-items: center;
        margin-bottom: 12px;

        .detail-title {
          font-weight: 600;
          color: #333;
          font-size: 16px;
        }
      }

      .detail-content {
        line-height: 1.6;
        color: #555;
        font-size: 14px;
      }
    }
  }
}

.ai-info {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px;
  padding: 20px;
  color: white;

  .ai-badge {
    display: flex;
    align-items: center;
    font-weight: 600;
    margin-bottom: 12px;
    font-size: 16px;
  }

  .ai-description {
    line-height: 1.6;
    opacity: 0.9;
    margin: 0;
    font-size: 14px;
  }
}

.button-container {
  display: flex;
  justify-content: center;
  padding-top: 20px;
  border-top: 1px solid #e9ecef;
  margin-top: 20px;
}

// 모바일 레이아웃 스타일
.mobile-layout {
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;

  .mobile-sticky-header {
    position: sticky;
    top: 0;
    z-index: 10;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 12px 16px;
    background: #f8f9fa;
    border-radius: 8px;
    margin-bottom: 12px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

    .header-title {
      display: flex;
      align-items: center;
      font-size: 16px;
      font-weight: 600;
      color: #333;
    }
  }

  .mobile-content-wrapper {
    flex: 1;
    overflow-y: auto;
    padding-bottom: 10px;
  }

  .mobile-risk-summary {
    background: white;
    border: 1px solid #e9ecef;
    border-radius: 12px;
    padding: 16px;
    margin-bottom: 16px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

    .mobile-risk-calculation {
      margin-bottom: 16px;

      .mobile-calc-single-row {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        flex-wrap: nowrap;

        .mobile-risk-factor {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          flex-shrink: 0;

          .factor-label {
            font-size: 11px;
            color: #666;
            margin-bottom: 4px;
            font-weight: 600;
            white-space: nowrap;
          }

          .factor-value {
            font-size: 16px;
            font-weight: 700;
            padding: 6px 10px;
            border-radius: 6px;
            min-width: 35px;
            text-align: center;
            border: 2px solid;

            &.possibility {
              background: #e3f2fd;
              color: #1976d2;
              border-color: #42a5f5;
            }

            &.severity {
              background: #fff3e0;
              color: #f57c00;
              border-color: #ff9800;
            }
          }
        }

        .mobile-operator,
        .mobile-equals {
          font-size: 16px;
          font-weight: 600;
          color: #666;
          flex-shrink: 0;
        }

        .mobile-total-risk {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          flex-shrink: 0;

          .total-label {
            font-size: 11px;
            color: #666;
            margin-bottom: 4px;
            font-weight: 600;
            white-space: nowrap;
          }

          .total-value {
            font-size: 18px;
            font-weight: 700;
            padding: 6px 12px;
            border-radius: 8px;
            min-width: 45px;
            text-align: center;
            border: 2px solid;

            &.risk-very-high {
              background: #ffebee;
              color: #c62828;
              border-color: #ef5350;
            }

            &.risk-high {
              background: #fff3e0;
              color: #ef6c00;
              border-color: #ff9800;
            }

            &.risk-medium {
              background: #fffde7;
              color: #f57f17;
              border-color: #ffeb3b;
            }

            &.risk-low {
              background: #e8f5e8;
              color: #2e7d32;
              border-color: #4caf50;
            }
          }
        }
      }
    }

    .mobile-management-standard {
      display: flex;
      justify-content: center;

      .mobile-standard-badge {
        display: flex;
        align-items: center;
        padding: 10px 16px;
        border-radius: 16px;
        font-weight: 600;
        font-size: 14px;
        border: 1px solid;

        &.risk-very-high {
          background: #ffebee;
          color: #c62828;
          border-color: #ef5350;
        }

        &.risk-high {
          background: #fff3e0;
          color: #ef6c00;
          border-color: #ff9800;
        }

        &.risk-medium {
          background: #fffde7;
          color: #f57f17;
          border-color: #ffeb3b;
        }

        &.risk-low {
          background: #e8f5e8;
          color: #2e7d32;
          border-color: #4caf50;
        }
      }
    }
  }

  .mobile-evaluation-section {
    margin-bottom: 16px;

    .mobile-section-title {
      display: flex;
      align-items: center;
      font-size: 16px;
      font-weight: 600;
      margin-bottom: 12px;
      color: #333;
      padding: 0 4px;
    }

    .mobile-evaluation-cards {
      .mobile-evaluation-card {
        background: white;
        border: 1px solid #e9ecef;
        border-radius: 8px;
        padding: 12px;
        margin-bottom: 8px;
        transition: all 0.2s ease;

        &:hover {
          border-color: #007bff;
          box-shadow: 0 2px 6px rgba(0, 123, 255, 0.1);
        }

        .mobile-evaluation-header {
          display: flex;
          align-items: center;
          margin-bottom: 8px;

          .mobile-evaluation-title {
            font-weight: 600;
            color: #333;
            font-size: 14px;
          }
        }

        .mobile-evaluation-content {
          line-height: 1.5;
          color: #555;
          font-size: 13px;
          word-break: break-word;
        }
      }
    }
  }

  .mobile-ai-info {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 12px;
    padding: 16px;
    color: white;
    margin-bottom: 16px;

    .mobile-ai-badge {
      display: flex;
      align-items: center;
      font-weight: 600;
      margin-bottom: 10px;
      font-size: 14px;
    }

    .mobile-ai-description {
      line-height: 1.5;
      opacity: 0.9;
      margin: 0;
      font-size: 13px;
    }
  }

  .mobile-bottom-spacer {
    height: 80px;
  }
}

.mobile-button-container {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #e9ecef;

  &.mobile-sticky-buttons {
    position: sticky;
    bottom: 0;
    z-index: 10;
    background: #f8f9fa;
    margin-top: 0;
    padding: 12px;
    border-top: 1px solid #e9ecef;
    box-shadow: 0 -2px 4px rgba(0, 0, 0, 0.1);
    display: flex;
    justify-content: center;
  }

  .mobile-button {
    min-height: 44px;
    font-size: 15px;
    font-weight: 600;
    border-radius: 8px;
    padding: 12px 24px;
    min-width: 140px;
    transition: all 0.3s ease;

    &.mobile-button-primary {
      background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
      color: white;
      border: none;
      box-shadow: 0 2px 6px rgba(0, 123, 255, 0.3);

      &:hover {
        box-shadow: 0 4px 12px rgba(0, 123, 255, 0.4);
        transform: translateY(-1px);
      }
    }
  }
}

// 모바일 환경에서 팝업 스타일 조정
@media (max-width: 767px) {
  #popupRiskAnalysis::v-deep {
    .vs-popup {
      .vs-popup--content {
        margin: 10px;
        max-height: 85vh;
        overflow-y: auto;
        padding: 10px !important;
      }
      .vs-popup--content {
        width: calc(100% - 20px);
      }

      .vs-popup--header {
        padding: 16px 20px 12px;

        .vs-popup--title {
          font-size: 18px;
          font-weight: 600;
        }
      }
    }
  }

  .mobile-layout {
    padding: 0;
  }

  .mobile-button-container {
    margin-left: -10px;
    margin-right: -10px;
    margin-bottom: -10px;
    padding: 10px;
    background: #f8f9fa;
    border-top: 1px solid #e9ecef;

    &.mobile-sticky-buttons {
      margin-left: -10px;
      margin-right: -10px;
      margin-bottom: -10px;
      padding: 12px;
      position: sticky;
      bottom: -10px;
      z-index: 10;
      background: #f8f9fa;
      border-top: 1px solid #e9ecef;
      box-shadow: 0 -2px 4px rgba(0, 0, 0, 0.1);
    }
  }
}

// PC 환경에서 기존 스타일 유지
@media (min-width: 768px) {
  #popupRiskAnalysis::v-deep {
    .vs-popup {
      min-width: 800px;

      .vs-popup--content {
        max-height: 90vh;
        overflow-y: auto;
      }
    }
  }
}
</style>
